import { Component, Prop, Ref } from 'vue-property-decorator'
import BaseSearcher from '@/components/common/searcher/components/base/base-searcher'
import {
    initialPatientSearcherState,
    PatientSearcherRequestParams,
    PatientSearcherState
} from '@/components/common/searcher/components/patient/patient-searcher.model'
import { SearcherStatus } from '@/components/common/searcher/shared/models/common-searcher.model'
import {
    IdentifierTypeResponse,
    IdentifierTypeScope
} from '@/api/resources/common/initialization/common-initialization.dto'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import { CommonCenterResponse } from '@/api/services/common/center/common-center.dto'
import { commonCenterService } from '@/api/services/common/center/common-center.service'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { store } from '@/store/store'
import { CustomValidationUtils } from '@/shared/util/custom-validation-utils'

export function initialPatientIdentifierTypeCode(): string {
    return StoreUtils.globalParameters?.mainPatientIdentifierType.code ?? ''
}
export async function getPatientIdentifierTypes(): Promise<IdentifierTypeResponse[]> {
    const response = await PromiseUtils.toCallSecure(PatientIdentifierResource.index())
    return response ?? []
}

export function initialPatientCenterCode(): string {
    return StoreUtils.selectedLaboratoryConfiguration?.center.code ?? ''
}
export async function getPatientCenters(): Promise<CommonCenterResponse[]> {
    const response = await PromiseUtils.toCallSecure(commonCenterService.getActiveCenters())
    return response ?? []
}

@Component
export default class PatientSearcher<T> extends BaseSearcher<PatientSearcherRequestParams, T> {
    @Prop({ type: String, default: 'patient-searcher' }) readonly qaId!: string
    @Prop({ type: Boolean, default: true }) readonly autofocus!: boolean

    @Prop({ type: Function, default: getPatientIdentifierTypes }) readonly getIdentifierTypes!: () => Promise<IdentifierTypeResponse[]>
    @Prop({ type: String, default: initialPatientIdentifierTypeCode }) readonly initialIdentifierTypeCode!: keyof IdentifierTypeResponse['code']

    @Prop({ type: Function, default: getPatientCenters }) readonly getCenters!: () => Promise<CommonCenterResponse[]>
    @Prop({ type: String, default: initialPatientCenterCode }) readonly initialCenterCode!: keyof CommonCenterResponse['code']

    @Prop({ type: Boolean, default: false }) readonly hideIdentifierTypeSelector!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideCenterSelector!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideIdentifierInput!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideNameInput!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideMiddleNameInput!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideSurname1Input!: boolean
    @Prop({ type: Boolean, default: false }) readonly hideSurname2Input!: boolean

    @Ref() readonly identifierInput!: any
    @Ref() readonly surname1Input!: any
    @Ref() readonly surname2Input!: any
    @Ref() readonly nameInput!: any
    @Ref() readonly middleNameInput!: any

    state: PatientSearcherState
    constructor() {
        super()
        this.state = initialPatientSearcherState()
    }

    get visibleIdentifierTypeSelector(): boolean {
        return !this.hideIdentifierTypeSelector &&
            !!this.state.identifierTypes.length
    }
    get visibleCenterSelector(): boolean {
        return !this.hideCenterSelector &&
            !!this.state.centers.length &&
            this.state.identifierType?.idtScope === IdentifierTypeScope.CENTER_PATIENT
    }
    get visibleIdentifierInput(): boolean {
        return !this.hideIdentifierInput
    }
    get visibleNameInput(): boolean {
        return !this.hideNameInput
    }
    get visibleMiddleNameInput(): boolean {
        return !this.hideMiddleNameInput &&
            !!StoreUtils.globalParameters?.middleNameSupported
    }
    get visibleSurname1Input(): boolean {
        return !this.hideSurname1Input
    }
    get visibleSurname2Input(): boolean {
        return !this.hideSurname2Input &&
            !!StoreUtils.globalParameters?.secondSurnameSupported
    }

    focus(): void {
        if (this.identifierInput) {
            this.identifierInput.focus()
        } else if (this.surname1Input) {
            this.surname1Input.focus()
        } else if (this.surname2Input) {
            this.surname2Input.focus()
        } else if (this.nameInput) {
            this.nameInput.focus()
        } else if (this.middleNameInput) {
            this.middleNameInput.focus()
        }
    }

    ///

    private _loadInitialValues(): void {
        const identifierType =
            this.state.identifierTypes.find(identifierType => identifierType.code === this.initialIdentifierTypeCode)
        if (identifierType) {
            this.state.identifierType = identifierType
        }

        const center = this.state.centers.find(center => center.code === this.initialCenterCode)
        if (center) {
            this.state.center = center
        }
    }

    /// VUE HOOKS ///

    async mounted(): Promise<void> {
        if (!this.hideIdentifierTypeSelector) {
            this.state.identifierTypes = await PromiseUtils.toCallSecure(this.getIdentifierTypes()) ?? []
        }
        if (!this.hideCenterSelector) {
            this.state.centers = await PromiseUtils.toCallSecure(this.getCenters()) ?? []
        }
        this._loadInitialValues()
        this.state.loaded = true

        if (this.autofocus) {
            this.focus()
        }
    }

    ///

    async search(): Promise<void> {
        const { id, name, middleName, surname1, surname2, identifierType, center } = this.state
        if (id || name || surname1 || surname2 || middleName) {
            const regexes =
                this.state.identifierType
                    ? store.state.regex.filter(regex => this.state.identifierType?.code === regex.identifierType.code)
                    : store.state.regex
            if (id && !CustomValidationUtils.checkRegex(regexes, id)) {
                this.searchStatus(SearcherStatus.INVALID_IDENTIFIER) // emits an event
                return
            }
            const params: PatientSearcherRequestParams =
                { id, name, middleName, surname1, surname2, identifierType, center }
            const response = await this.internalSearch(this.searcher, params)
            this.searched(response ?? []) // emits an event
            if (response) {
                if (!response.length) {
                    this.searchStatus(SearcherStatus.NO_RESULTS) // emits an event
                } else {
                    this.searchStatus(SearcherStatus.SEARCH_SUCCESS) // emits an event
                }
            }
        }
    }

    clearSearch(): void {
        const { status, id, name, middleName, surname1, surname2 } = initialPatientSearcherState()
        this.state = {
            ...this.state,
            status,
            id,
            name,
            middleName,
            surname1,
            surname2
        }
        this._loadInitialValues()
        this.searchStatus(SearcherStatus.SEARCH_CLEARED) // emits an event

        if (this.autofocus) {
            this.focus()
        }
    }
}
